/**
 * ``MaximizeToggle`` — a maximize/restore toggle button for a dockview
 * group header (REQ-UI-059).
 *
 * Gives every panel group a visual affordance for the maximize/restore
 * behaviour that was previously keyboard-only (``Ctrl+Shift+M`` per
 * REQ-UI-059, wired in :file:`multi-pane-workspace.tsx`). It lives in the
 * group header's right-actions slot (see :func:`PanelHeaderActions`), so
 * it renders once per group — for Meridian's single-panel-per-group
 * presets that reads as "one control per panel".
 *
 * # Group-scoped (dockview model)
 *
 * Maximize in dockview is a *group* operation: maximizing shows the whole
 * group (its tab strip + active panel) full-bleed over the workspace. This
 * button toggles **its own group** via the group api
 * (:meth:`DockviewGroupPanelApi.maximize` / ``exitMaximized`` /
 * ``isMaximized``). While a group is maximized only that group's header is
 * visible, so its button is the one showing the restore affordance.
 *
 * # Reactivity
 *
 * The icon/label must reflect the live maximize state — which can change
 * from this button, the keyboard shortcut, or another group's button. We
 * subscribe to the container's ``onDidMaximizedGroupChange`` and re-read
 * ``api.isMaximized()`` so the toggle stays in sync regardless of who
 * changed it.
 *
 * # Parity with the keyboard path
 *
 * Clicking announces ``"Panel maximized."`` / ``"Panel restored."`` via the
 * shared :func:`useAnnouncer`, identical to the ``Ctrl+Shift+M`` handler, so
 * screen-reader users get the same cue from either path (REQ-UI-059).
 *
 * Cross-references:
 *   - REQ-UI-059 (maximize/restore + screen-reader announcement).
 *   - ADR-024 §8 (multi-pane workspace; panel-header affordances).
 *   - multi-pane/multi-pane-workspace.tsx §Ctrl+Shift+M (shared behaviour).
 *   - docs/engineering/detail/storyboards/shared/chrome-spec.md §Panel header.
 */
import { useEffect, useState } from "react";
import { CornersIn, CornersOut } from "@phosphor-icons/react";
import type { IDockviewHeaderActionsProps } from "dockview-react";
import { useAnnouncer } from "@/chrome/announcer";

/**
 * Render the maximize/restore toggle for the group described by ``props``.
 *
 * @param props - dockview header-actions props; reads the group api
 *     (``api``) + the container api (``containerApi``) for the
 *     maximized-change event.
 */
export function MaximizeToggle(props: IDockviewHeaderActionsProps): React.ReactElement {
  const { api, containerApi } = props;
  const { announce } = useAnnouncer();
  const [maximized, setMaximized] = useState<boolean>(() => api.isMaximized());

  useEffect(() => {
    // Re-sync immediately (the group instance may have changed) and on
    // every maximized-group change — from this button, the keyboard
    // shortcut, or another group.
    setMaximized(api.isMaximized());
    const disposable = containerApi.onDidMaximizedGroupChange(() => {
      setMaximized(api.isMaximized());
    });
    return () => {
      disposable.dispose();
    };
  }, [api, containerApi]);

  const handleClick = (): void => {
    // Read the authoritative state at click time rather than trusting the
    // rendered ``maximized`` (defensive against a missed event).
    if (api.isMaximized()) {
      api.exitMaximized();
      announce("Panel restored.");
    } else {
      api.maximize();
      announce("Panel maximized.");
    }
  };

  const label = maximized ? "Restore panel" : "Maximize panel";
  const Icon = maximized ? CornersIn : CornersOut;

  return (
    <button
      type="button"
      data-testid="panel-maximize-toggle"
      aria-label={label}
      aria-pressed={maximized}
      title={label}
      onClick={handleClick}
      className={[
        // Header chrome — full-height, no background of its own; matches
        // the map-settings gear button alongside it.
        "flex h-full items-center rounded px-2 text-text-secondary",
        "hover:text-text-primary focus-visible:text-text-primary",
        "outline-none",
      ].join(" ")}
    >
      <Icon size={16} />
    </button>
  );
}
